import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getMentors } from "@/services/mentorService.ts";
import { queryKeys } from "@/services/queryKeyFactory.ts";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardDescription, CardHeader } from "@/components/ui/card.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { useTranslation } from "react-i18next";
import { Mentor } from "@/types/Mentor.ts";
import { adjustTimezone, timezoneResolver } from "@/utils/time-helper.ts";
import { activeLocale, getLocalizedFieldName, LocalizedField } from "@/utils/translation-helper.ts";
import { Calendar, Users } from "lucide-react";
import { WhatsAppIcon } from "@/components/Icons.tsx";
import pluralize from "pluralize";
import { getImgSrc } from "@/utils/file-upload.ts";

type MentorshipGroupsPageProps = {
    seasonId?: number;
};

/** Find the next upcoming availability date from today. */
function getNextSessionDate(availabilities: Mentor["availabilities"]): Date | null {
    if (!availabilities?.length) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const upcoming = availabilities
        .map((a) => adjustTimezone(a.availableOn))
        .filter((d) => d >= now)
        .sort((a, b) => a.getTime() - b.getTime());
    return upcoming[0] ?? null;
}

export const MentorshipGroupsPage = ({ seasonId }: MentorshipGroupsPageProps) => {
    const { t, i18n } = useTranslation();

    const { data: mentors = [], isFetching } = useQuery({
        queryKey: [...queryKeys.fetchMentors.all, seasonId ?? "active"],
        queryFn: () => getMentors("?include=mentorship-group", seasonId),
        initialData: [],
    });

    const mentorsWithGroups = useMemo(
        () => mentors.filter((m) => m.mentees && m.mentees.length > 0),
        [mentors],
    );

    if (isFetching) {
        return (
            <div className="p-6 pt-0 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-48 w-full rounded-xl" />
                ))}
            </div>
        );
    }

    if (mentorsWithGroups.length === 0) {
        return (
            <Card className="ml-6">
                <CardHeader>
                    <CardDescription className="text-base">
                        {t("dashboard.noMentorshipGroups")}
                    </CardDescription>
                </CardHeader>
            </Card>
        );
    }

    const locale = activeLocale();
    const affinityField = getLocalizedFieldName("name");

    return (
        <div className="p-6 pt-0 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {mentorsWithGroups.map((mentor) => {
                const nextSession = getNextSessionDate(mentor.availabilities);
                const tz = mentor.timezone
                    ? timezoneResolver[mentor.timezone]?.[locale as keyof LocalizedField] ?? mentor.timezone
                    : null;
                const affinityName = mentor.affinityGroup
                    ? (mentor.affinityGroup as Record<string, unknown>)[affinityField] as string ?? mentor.affinityGroup.name
                    : null;
                const menteeCount = mentor.mentees?.length ?? 0;

                return (
                    <Link
                        key={mentor.id}
                        to={`/mentors/${mentor.id}/mentees`}
                        className="block group focus-visible:ring-2 focus-visible:ring-accent/40 rounded-xl"
                    >
                        <Card className="h-full transition-colors group-hover:bg-bg-elevated/50">
                            <div className="p-4 space-y-3">
                                {/* Mentor header */}
                                <div className="flex items-start gap-3">
                                    {mentor.user.profilePictureSrc ? (
                                        <img
                                            src={getImgSrc(mentor.user.profilePictureSrc)}
                                            alt={mentor.user.fullName}
                                            className="h-12 w-12 rounded-full object-cover shrink-0 border border-border"
                                        />
                                    ) : (
                                        <div className="h-12 w-12 rounded-full bg-bg-elevated border border-border flex items-center justify-center shrink-0">
                                            <span className="text-sm font-semibold text-text-2">
                                                {mentor.user.firstName?.[0]}{mentor.user.lastName?.[0]}
                                            </span>
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold text-text-1 truncate">
                                            {mentor.user.fullName}
                                        </div>
                                        {mentor.title && (
                                            <div className="text-xs text-text-2 truncate">
                                                {(mentor as Record<string, unknown>)[getLocalizedFieldName("title")] as string ?? mentor.title}
                                            </div>
                                        )}
                                        {mentor.university && (
                                            <div className="text-xs text-text-2 truncate">
                                                {(mentor as Record<string, unknown>)[getLocalizedFieldName("university")] as string ?? mentor.university}
                                            </div>
                                        )}
                                    </div>
                                    {mentor.isOnWhatsApp && (
                                        <span title={t("mentor.availableOnWhatsApp")} className="shrink-0 mt-0.5 inline-flex">
                                            <WhatsAppIcon
                                                className="h-5 w-5 text-[#25D366]"
                                                title={t("mentor.availableOnWhatsApp")}
                                            />
                                        </span>
                                    )}
                                </div>

                                {/* Badges row: affinity group stands out, timezone secondary */}
                                <div className="flex flex-wrap items-center gap-2">
                                    {affinityName && (
                                        <Badge
                                            className="rounded-full px-3 py-1 text-xs font-semibold bg-accent text-bg-card border-0 shadow-sm"
                                            title={t("affinity.group")}
                                            aria-label={t("affinity.group")}
                                        >
                                            {affinityName}
                                        </Badge>
                                    )}
                                    {tz && (
                                        <span className="text-xs text-text-2 truncate">{tz}</span>
                                    )}
                                </div>

                                {/* Next session */}
                                {nextSession && (
                                    <div className="flex items-center gap-1.5 rounded-md bg-springGreen/10 px-2.5 py-1.5 text-xs font-medium text-springGreen">
                                        <Calendar className="h-3.5 w-3.5 shrink-0" />
                                        <span>
                                            {t("dashboard.nextMeeting")}: {nextSession.toLocaleDateString(i18n.language, { month: "short", day: "numeric" })}
                                        </span>
                                    </div>
                                )}

                                {/* Mentee avatars + count */}
                                <div className="flex items-center gap-2 pt-1 border-t border-border">
                                    <div className="flex -space-x-2">
                                        {mentor.mentees?.slice(0, 5).map((mentee) => (
                                            mentee.user.profilePictureSrc ? (
                                                <img
                                                    key={mentee.id}
                                                    src={getImgSrc(mentee.user.profilePictureSrc)}
                                                    alt={mentee.user.fullName}
                                                    title={mentee.user.fullName}
                                                    className="h-7 w-7 rounded-full border-2 border-bg-card object-cover"
                                                />
                                            ) : (
                                                <div
                                                    key={mentee.id}
                                                    title={mentee.user.fullName}
                                                    className="h-7 w-7 rounded-full border-2 border-bg-card bg-bg-elevated flex items-center justify-center"
                                                >
                                                    <span className="text-[10px] font-medium text-text-2">
                                                        {mentee.user.firstName?.[0]}{mentee.user.lastName?.[0]}
                                                    </span>
                                                </div>
                                            )
                                        ))}
                                        {menteeCount > 5 && (
                                            <div className="h-7 w-7 rounded-full border-2 border-bg-card bg-bg-elevated flex items-center justify-center">
                                                <span className="text-[10px] font-medium text-text-2">+{menteeCount - 5}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1 text-xs text-text-2 ml-auto">
                                        <Users className="h-3.5 w-3.5" />
                                        <span>{pluralize("mentee", menteeCount, true)}</span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </Link>
                );
            })}
        </div>
    );
};
